import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip,
  AttributionControl,
  LayersControl,
} from "react-leaflet";
import axios from "axios";
import { useHistory } from "react-router-dom";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";

import { renderToStaticMarkup } from "react-dom/server";
import { divIcon } from "leaflet";
import RoomIcon from "@mui/icons-material/Room";

import { useIntl } from "react-intl";
const defaults = require("../../translations/en.json");

// require('~leaflet/dist/leaflet.css'); // inside .js file
require("@changey/react-leaflet-markercluster/dist/styles.min.css"); // inside .js file

// デフォルト位置: 日本の中心（長野県）
const center = [36.2048, 138.2529];

const DisplayPosition = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    position,
    getMarkerPosition,
  }));

  const { mapRef, refDraggableMarker } = props;

  const [position, setPosition] = useState(center);

  const getMarkerPosition = useCallback(() => {
    const marker = refDraggableMarker.current.markerRef.current;
    if (marker != null) {
      refDraggableMarker.current.setMarkerPosition(marker.getLatLng());
    }

    let lng;
    lng = refDraggableMarker.current.markerPosition.lng % 360;
    if (lng > 180) {
      lng = lng - 360;
    } else if (lng < -180) {
      lng = lng * 360;
    }

    // let reverseGeoLocationURL = `https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress?lat=43.0686718333333&lon=141.351173694444`
    // let reverseGeoLocationURL = 'https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress?lat=' + refDraggableMarker.current.markerPosition.lat + '&lon=' + refDraggableMarker.current.markerPosition.lng
    // let reverseGeoLocationURL = 'https://nominatim.openstreetmap.org/reverse.php?lat=35.87695&lon=139.64798&zoom=18&format=jsonv2';
    let reverseGeoLocationURL =
      "https://nominatim.openstreetmap.org/reverse.php?lat=" +
      refDraggableMarker.current.markerPosition.lat +
      "&lon=" +
      lng +
      "&zoom=18&format=jsonv2";

    axios
      // .get(`https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress?lat=43.0686718333333&lon=141.351173694444`)             //リクエストを飛ばすpath
      .get(reverseGeoLocationURL, {
        headers: {
          Accept: "application/json",
        },
      }) //リクエストを飛ばすpath
      .then((response) => {
        setApiResult(response.data.display_name);
      }) //成功した場合、postsを更新する（then）
      .catch((err) => {
        // 逆ジオコーディング失敗（エラーログのみ）
      }); //失敗した場合(catch)
  }, [refDraggableMarker]);

  const onMove = useCallback(
    (e) => {
      if (!mapRef.current) return;
      let c = mapRef.current.getCenter();
      setPosition(c);
    },
    [mapRef]
  );

  const onLocationFound = useCallback(
    (e) => {
      if (!mapRef.current) return;
      mapRef.current.flyTo(e.latlng, mapRef.current.getZoom());
    },
    [mapRef]
  );

  const onLocationError = useCallback((e) => {
    window.alert("位置情報取得に失敗しました。");
  }, []);

  useEffect(() => {
    // jumpCurrentPosition();
    // refDisplayPosition.current.getMarkerPosition();
  }, []);

  const [apiResult, setApiResult] = useState("");

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    map.on("move", onMove);
    map.on("locationfound", onLocationFound);
    map.on("locationerror", onLocationError);

    return () => {
      map.off("move", onMove);
      map.off("locationfound", onLocationFound);
      map.off("locationerror", onLocationError);
    };
  }, [mapRef, onMove, apiResult, onLocationFound, onLocationError, refDraggableMarker]);

  return <React.Fragment></React.Fragment>;
});

const DraggableMarker = forwardRef((props, ref) => {
  const { article } = props;
  const history = useHistory();

  const iconMarkup = renderToStaticMarkup(
    // <RoomIcon fontSize='large' color='secondary'/>
    <RoomIcon style={{ fontSize: 50 }} color="secondary" />
  );
  const customMarkerIcon = divIcon({
    html: iconMarkup,
    className: "hogehoge", // CSSのクラス名。指定しないとデフォルトのleaflet-div-iconというクラスがつき、background: #fff;border: 1px solid #666;というスタイルがつきます
    iconSize: [50, 90], // iconSizeを指定しないと、マーカーの中心でなく、左上が指定した座標に来るようになります
  });

  return !article.latitude ? null : (
    <Marker
      icon={customMarkerIcon}
      draggable={true}
      eventHandlers={{
        click: (e) => {
          history.push("/article/" + article.id);
        },
      }}
      position={[article.latitude, article.longitude]}
    >
      <Tooltip>{article.name}</Tooltip>
    </Marker>
  );
});

function SelectLocation(props) {
  const { articles, filterLabelName } = props;

  const refDisplayPosition = useRef();
  const refDraggableMarker = useRef();
  const mapRef = useRef(null);

  // 常にデフォルト位置とズームで開始（セッションストレージは使用しない）
  const [initialPosition] = useState(center);
  const [initialZoom] = useState(6);

  const hasFitBoundsRef = useRef(false);

  React.useEffect(() => {
    console.log("Map bounds effect triggered, articles count:", articles.length);

    // 既に一度実行済みの場合はスキップ
    if (hasFitBoundsRef.current) {
      console.log("Already fitted bounds, skipping");
      return;
    }

    // マップの準備ができていない場合は待つ
    if (!mapRef.current) {
      console.log("Map ref not ready, waiting");
      return;
    }

    // 記事が読み込まれるのを待つ
    if (articles.length === 0) {
      console.log("No articles yet, waiting");
      return;
    }

    // 位置情報を持つ記事を抽出
    const articlesWithLocation = articles.filter(
      (article) => article.latitude != null && article.longitude != null
    );

    console.log("Articles with location:", articlesWithLocation.length);

    if (articlesWithLocation.length === 0) {
      console.log("No articles with location");
      return;
    }

    // 記事の境界を計算
    const lats = articlesWithLocation.map((a) => a.latitude);
    const lngs = articlesWithLocation.map((a) => a.longitude);
    const maxLat = Math.max(...lats);
    const minLat = Math.min(...lats);
    const maxLng = Math.max(...lngs);
    const minLng = Math.min(...lngs);

    // マップが完全に初期化されるまで待つ
    const executeWhenReady = () => {
      if (!mapRef.current) {
        console.log("Map ref not ready");
        return;
      }

      try {
        mapRef.current.whenReady(() => {
          if (hasFitBoundsRef.current) {
            console.log("Already fitted bounds");
            return;
          }

          console.log("Fitting bounds to:", {
            north: maxLat,
            east: maxLng,
            south: minLat,
            west: minLng,
            articlesCount: articlesWithLocation.length,
          });

          mapRef.current.flyToBounds(
            [
              [maxLat, maxLng],
              [minLat, minLng],
            ],
            { duration: 0.5, padding: [50, 50] }
          );
          hasFitBoundsRef.current = true;
        });
      } catch (err) {
        console.error("flyToBounds error:", err);
      }
    };

    // 少し遅延させてマップの初期化を待つ
    const timer = setTimeout(executeWhenReady, 100);

    return () => clearTimeout(timer);
  }, [articles]);

  React.useEffect(() => {
    // vh初期化
    setFillHeight();
  }, []);

  const setFillHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  // 画面のサイズ変動があった時に高さを再計算する
  window.addEventListener("resize", setFillHeight);

  const { formatMessage } = useIntl();

  const displayMap = useMemo(
    () => (
      <MapContainer
        ref={mapRef}
        center={initialPosition}
        zoom={initialZoom}
        scrollWheelZoom={false}
        style={{ height: "calc( ( var(--vh, 1vh) * 100 ) - 56px - 56px)" }}
      >
        <AttributionControl position="bottomleft" prefix={false} />
        <LayersControl position="bottomleft">
          <LayersControl.BaseLayer
            checked
            name={formatMessage({
              id: "map",
              defaultMessage: defaults["map"],
            })}
          >
            <TileLayer
              attribution="<a href='https://developers.google.com/maps/documentation' target='_blank'>Google Map</a>"
              url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer
            name={formatMessage({
              id: "map_picture",
              defaultMessage: defaults["map_picture"],
            })}
          >
            <TileLayer
              attribution="<a href='https://developers.google.com/maps/documentation' target='_blank'>Google Map</a>"
              url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer
            name={formatMessage({
              id: "picture",
              defaultMessage: defaults["picture"],
            })}
          >
            <TileLayer
              attribution="<a href='https://developers.google.com/maps/documentation' target='_blank'>Google Map</a>"
              url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
            />
          </LayersControl.BaseLayer>
          <MarkerClusterGroup>
            {articles.map(
              (article) =>
                !!article &&
                (!filterLabelName ||
                  article.labels.items.find((item) => {
                    return !!item && !!item.label && item.label.name === filterLabelName;
                  })) && <DraggableMarker key={article.id} article={article} />
            )}
          </MarkerClusterGroup>
        </LayersControl>
      </MapContainer>
    ),
    [articles, filterLabelName, formatMessage, initialPosition, initialZoom]
  );

  return (
    <div>
      {mapRef.current ? (
        <DisplayPosition
          ref={refDisplayPosition}
          mapRef={mapRef}
          refDraggableMarker={refDraggableMarker}
        />
      ) : null}
      {/* {!maxLat || !maxLng || !minLat || !minLng ? (
        <div>位置情報が存在しません</div>
      ) : ( */}
      {displayMap}
      {/* )} */}
    </div>
  );
}

export default SelectLocation;
